//@author: travis
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    model,
    ViewEncapsulation,
} from "@angular/core";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Item } from "../../../pojo/Item";
import { Nullable } from "../../../pojo/Util";
import { ContactService } from "../../../services/contact.service";
import { OrganizationService } from "../../../services/organization.service";
import { SearchCriteriaService } from "../../../services/search/search-criteria/search.criteria.service";
import { AriaLabel } from "../../../util/aria/aria.label";
import { S25Util } from "../../../util/s25-util";
import { s25Resource } from "../../../util/s25.resource";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { DropdownDataUtil } from "../dropdown.data.util";

/** ANG-5116 primary-org allowlist: presence means restricted mode. */
export interface OrgDropdownRestriction {
    /** Explicit org IDs from form config `organizations_allowed` */
    orgIds: number[];
    /**
     * Role IDs from `org_roles_allowed`, or `"any"` when config allows any
     * associated org regardless of role (matches the literal config value).
     * `"any"` === `[]` for load behavior (no role filter on associated orgs).
     */
    roleIds: number[] | "any";
}

@TypeManagerDecorator("s25-organization-dropdown")
@Component({
    selector: "s25-organization-dropdown",
    template: `
        <s25-generic-dropdown
            [value]="value()"
            [hasFav]="true"
            [onSelect]="onSelect()"
            [items]="items()"
            [staticItems]="items()"
            [focusFirst]="false"
            [searchEnabled]="true"
            [serverSide]="serverSide()"
            [placeholder]="'Select an Organization'"
            [searchPlaceholder]="'Filter Organizations'"
            [hasNoneItem]="hasNoneItem()"
            [noneItemLabel]="noneItemLabel()"
            [readOnly]="readOnly()"
            [blockDetailsNav]="blockDetailsNav()"
            [renderItem]="renderItem()"
            [ariaLabel]="ariaLabel()"
            (valueChange)="onValueChange($event)"
        />
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class S25OrganizationDropdownComponent {
    readonly restriction = input<OrgDropdownRestriction | undefined>();

    readonly value = model<Nullable<DropDownItem>>();
    readonly onSelect = input<(item: Nullable<DropDownItem>) => void>();
    readonly onLoad = input<() => void>();
    readonly customFilterValue = input<string>();
    readonly hasNoneItem = input(false);
    readonly noneItemLabel = input("None");
    readonly readOnly = input(false);
    readonly blockDetailsNav = input(false);
    readonly renderItem = input(true);
    readonly ariaLabel = input<AriaLabel>();

    private readonly elementRef = inject(ElementRef);

    readonly isRestricted = computed(() => !!this.restriction());

    /** Favorites (and unrestricted static list) — needed with or without restrictions. */
    readonly favorites = s25Resource({
        params: () => this.customFilterValue() ?? "",
        loader: ({ params: customFilterValue }) =>
            SearchCriteriaService.getEventOrganizations(-999, 0, 999, null, true, null, customFilterValue).then(
                (data) => (data && data.searchCriteria && data.searchCriteria.item) || [],
            ),
    });

    readonly associatedOrgs = s25Resource({
        if: () => this.isRestricted(),
        params: () => this.restriction()?.roleIds,
        loader: ({ params: roleFilter }) =>
            ContactService.getCurrentAssociatedOrganizations(
                roleFilter === "any" || !roleFilter?.length ? undefined : roleFilter,
            ),
    });

    readonly orgsById = s25Resource({
        if: () => this.isRestricted(),
        params: () => this.restriction()!.orgIds,
        loader: ({ params: orgIds }) =>
            orgIds?.length ? OrganizationService.getOrganizationsById(orgIds) : Promise.resolve([]),
    });

    readonly items = computed(() => {
        if (this.isRestricted()) {
            return this.restrictedAllowlist();
        } else {
            return S25Util.deepCopy(this.favorites.value() ?? []);
        }
    });

    readonly serverSide = computed(() => {
        if (this.isRestricted() || this.favorites.value() === undefined) {
            return undefined;
        }
        return DropdownDataUtil.getServerSideModel(
            "getEventOrganizations",
            this.elementRef.nativeElement,
            this.customFilterValue(),
        );
    });

    readonly favoriteByItemId = computed(() => {
        const favorites = this.favorites.value();
        return new Map<number, DropDownItem>(
            favorites?.map((favorite: DropDownItem) => [Number(favorite.itemId), favorite]),
        );
    });

    constructor() {
        effect(() => {
            if (this.value()) {
                this.value()!.itemTypeId = Item.Ids.Organization;
            }
        });

        // Parent onLoad once items are ready (unrestricted favorites or full restricted merge)
        const onLoadEffect = effect(() => {
            if (
                this.onLoad() &&
                !this.favorites.isLoading() &&
                !this.associatedOrgs.isLoading() &&
                !this.orgsById.isLoading()
            ) {
                this.onLoad()!();
                onLoadEffect.destroy();
            }
        });
    }

    onValueChange(data?: DropDownItem | null) {
        this.value.set(data ?? null);
    }

    private restrictedAllowlist(): DropDownItem[] {
        const favoriteByItemId = this.favoriteByItemId();
        const allowlistById = new Map<number, DropDownItem>();

        const allowedOrgs = [...(this.associatedOrgs.value() ?? []), ...(this.orgsById.value() ?? [])];
        for (const org of allowedOrgs) {
            const orgId = Number(org.organization_id);
            allowlistById.set(orgId, {
                itemId: orgId,
                itemName: org.organization_name,
                itemTypeId: Item.Ids.Organization,
                favorite: favoriteByItemId.get(orgId)?.favorite ?? "F",
            });
        }

        return Array.from(allowlistById.values());
    }
}
